/**
 * RxNorm REST API client
 *
 * Free, unauthenticated NIH API for medication lookup.
 * Covers every FDA-approved drug with RxCUI identifiers.
 *
 * Base URL: https://rxnav.nlm.nih.gov/REST/
 * Docs: https://lhncbc.nlm.nih.gov/RxNav/APIs/RxNormAPIs.html
 */

import type {
  RxNormDrugsResponse,
  RxNormApproximateResponse,
  RxNormPropertiesResponse,
  RxNormSearchResult,
} from "./types";

const BASE_URL = "https://rxnav.nlm.nih.gov/REST";

// Term types we care about (filter out noise like ingredient-only or dose form groups)
const RELEVANT_TTY = new Set([
  "SBD", // Semantic Branded Drug (e.g., "Letrozole 2.5 MG Oral Tablet [Femara]")
  "SCD", // Semantic Clinical Drug (e.g., "Letrozole 2.5 MG Oral Tablet")
  "GPCK", // Generic Pack
  "BPCK", // Brand Pack
  "IN", // Ingredient (useful for matching generic names)
  "BN", // Brand Name
]);

/**
 * Search drugs by name using the /drugs endpoint.
 * Returns clinical and branded drug concepts matching the query.
 */
export async function searchDrugs(
  query: string,
): Promise<RxNormSearchResult[]> {
  const url = `${BASE_URL}/drugs.json?name=${encodeURIComponent(query)}`;

  const response = await fetch(url);
  if (!response.ok) return [];

  const data: RxNormDrugsResponse = await response.json();

  const results: RxNormSearchResult[] = [];
  for (const group of data.drugGroup?.conceptGroup ?? []) {
    if (!RELEVANT_TTY.has(group.tty)) continue;
    for (const concept of group.conceptProperties ?? []) {
      results.push({
        rxcui: concept.rxcui,
        name: concept.name,
        tty: concept.tty,
      });
    }
  }

  return results;
}

/**
 * Fuzzy/approximate search using the /approximateTerm endpoint.
 * Handles typos and partial matches.
 */
export async function approximateSearch(
  term: string,
  maxEntries = 10,
): Promise<RxNormSearchResult[]> {
  const url = `${BASE_URL}/approximateTerm.json?term=${encodeURIComponent(term)}&maxEntries=${maxEntries}`;

  const response = await fetch(url);
  if (!response.ok) return [];

  const data: RxNormApproximateResponse = await response.json();
  const candidates = data.approximateGroup?.candidate ?? [];

  // Approximate endpoint returns rxcui but not names — fetch properties for each
  const results: RxNormSearchResult[] = [];
  const seen = new Set<string>();

  for (const candidate of candidates.slice(0, maxEntries)) {
    if (seen.has(candidate.rxcui)) continue;
    seen.add(candidate.rxcui);

    const props = await fetchProperties(candidate.rxcui);
    if (props && RELEVANT_TTY.has(props.tty)) {
      results.push({
        rxcui: props.rxcui,
        name: props.name,
        tty: props.tty,
      });
    }
  }

  return results;
}

/**
 * Fetch full properties for a specific RxCUI.
 * Used after selection to auto-fill medication details.
 */
export async function fetchProperties(
  rxcui: string,
): Promise<RxNormSearchResult | null> {
  const url = `${BASE_URL}/rxcui/${encodeURIComponent(rxcui)}/properties.json`;

  const response = await fetch(url);
  if (!response.ok) return null;

  const data: RxNormPropertiesResponse = await response.json();
  if (!data.properties) return null;

  return {
    rxcui: data.properties.rxcui,
    name: data.properties.name,
    tty: data.properties.tty,
  };
}

/**
 * Parse a drug name string to extract dosage info.
 * RxNorm names follow patterns like:
 *   "Letrozole 2.5 MG Oral Tablet [Femara]"
 *   "Progesterone 200 MG Vaginal Capsule"
 *
 * Returns parsed fields or null if pattern doesn't match.
 */
export function parseDrugName(fullName: string): {
  name: string;
  dose: string;
  doseUnit: string;
  route: string;
  form: string;
  brandName: string | null;
} | null {
  // Pattern: "Drug Dose Unit Route Form [Brand]"
  const match = fullName.match(
    /^(.+?)\s+(\d+(?:\.\d+)?)\s+(MG|MCG|ML|IU|UNITS?|MG\/ML|MCG\/ML)\s+(.+?)(?:\s+\[(.+?)\])?$/i,
  );

  if (!match) {
    // Simpler pattern: just "Drug Name [Brand]"
    const simpleBrand = fullName.match(/^(.+?)\s+\[(.+?)\]$/);
    if (simpleBrand) {
      return {
        name: simpleBrand[1]!.trim(),
        dose: "",
        doseUnit: "",
        route: "",
        form: "",
        brandName: simpleBrand[2]!.trim(),
      };
    }
    return null;
  }

  const routeForm = match[4]!.trim();
  // Common route/form patterns
  const routeMap: Record<string, string> = {
    oral: "oral",
    vaginal: "intravaginal",
    subcutaneous: "subcutaneous",
    intramuscular: "intramuscular",
    transdermal: "transdermal",
    intranasal: "intranasal",
    topical: "topical",
    sublingual: "sublingual",
    intravenous: "intravenous",
  };

  let route = "";
  const lowerRouteForm = routeForm.toLowerCase();
  for (const [key, value] of Object.entries(routeMap)) {
    if (lowerRouteForm.includes(key)) {
      route = value;
      break;
    }
  }

  return {
    name: match[1]!.trim(),
    dose: match[2]!,
    doseUnit: match[3]!.toLowerCase(),
    route,
    form: routeForm,
    brandName: match[5]?.trim() ?? null,
  };
}
